import { EnumsOf } from "@/lib/utils"
import { StateCreator } from "@/store/utils"

export interface ProcessSession {
  id: string
  processId: string
  processName: string
  status: ShiftStatus
  startedAt: string
  updatedAt: string
  endedAt: string | null
}

export const ShiftStatus = {
  Started: "STARTED",
  Draft: "DRAFT",
  Ended: "ENDED",
  Deleted: "DELETED",
  Inactive: "INACTIVE",
} as const
export type ShiftStatus = EnumsOf<typeof ShiftStatus>

export interface SessionState {
  shift: ShiftDetails
  session: SessionDetails
  pendingIpqcDiscardProcessId: string
  pendingBoiloutDiscardProcessId: string
}

export interface SessionDetails {
  id: string
  status: ShiftStatus
  startedAt: string
  endedAt: string | null
  processes: ProcessSession[]
}

export interface ShiftDetails {
  id: string
  name: string
  startedAt: string
  endedAt: string
}

export type ShiftDetailsParams = SessionState["shift"]

export interface SessionSlice {
  session: SessionState & {
    setShiftDetails(details: ShiftDetailsParams): void
    setProcessUpdatedAt(processId: string, timestamp: string): void
    endShift(): void
    setSession(session: SessionDetails | null): void
    mergeSession(session: SessionDetails | null): void
    addProcess(process: ProcessSession): void
    startProcess(processId: string): void
    startShift(shiftSessionId: string): void
    endProcess(processId: string, timestamp?: string): void
    setPendingIpqcDiscard(processId: string): void
    clearPendingIpqcDiscard(): void
    setPendingBoiloutDiscard(processId: string): void
    clearPendingBoiloutDiscard(): void
  }
}

function actionName(action: string) {
  return `session/${action}`
}

export const initialState: () => SessionState = () => ({
  shift: {
    id: "",
    updatedAt: "",
    name: "",
    startedAt: "",
    endedAt: "",
  },
  session: {
    id: "",
    status: ShiftStatus.Inactive,
    startedAt: "",
    endedAt: "",
    processes: [],
  },
  pendingIpqcDiscardProcessId: "",
  pendingBoiloutDiscardProcessId: "",
  processes: [],
})

const createSessionSlice: StateCreator<SessionSlice> = (set, __get) => ({
  session: {
    ...initialState(),
    setShiftDetails(details) {
      set(
        (state) => {
          state.session.shift = details
        },
        undefined,
        actionName("setShiftDetails")
      )
    },
    endShift() {
      set(
        (state) => {
          state.session = {
            ...state.session,
            ...initialState(),
          }
        },
        undefined,
        actionName("endSession")
      )
    },
    setSession(session) {
      set(
        (state) => {
          state.session.session = session || initialState().session

          return state
        },
        undefined,
        actionName("setSession")
      )
    },
    mergeSession(session) {
      set(
        (state) => {
          if (!session) {
            state.session.session = initialState().session
            return state
          }

          // Update session-level fields from server
          state.session.session.id = session.id
          state.session.session.status = session.status
          state.session.session.startedAt = session.startedAt
          state.session.session.endedAt = session.endedAt

          // Create a map of existing local processes by processId
          const localProcessMap = new Map<string, ProcessSession>()
          for (const process of state.session.session.processes) {
            localProcessMap.set(process.processId, process)
          }

          // Update existing processes with server data
          for (const serverProcess of session.processes) {
            const localProcess = localProcessMap.get(serverProcess.processId)
            if (localProcess) {
              // Merge server data into existing local process
              localProcess.id = serverProcess.id
              localProcess.processName = serverProcess.processName
              localProcess.status = serverProcess.status
              localProcess.startedAt = serverProcess.startedAt
              localProcess.updatedAt = serverProcess.updatedAt
              localProcess.endedAt = serverProcess.endedAt
              // Mark as processed
              localProcessMap.delete(serverProcess.processId)
            } else {
              // New process from server - add it
              state.session.session.processes.push(serverProcess)
            }
          }

          // Local-only processes remain untouched (they're still in the array)
          // This handles the case where a process was just added locally but not yet synced

          const latestByProcessId = new Map<string, ProcessSession>()
          for (const process of state.session.session.processes) {
            const existing = latestByProcessId.get(process.processId)

            if (!existing) {
              latestByProcessId.set(process.processId, process)
              continue
            }

            const existingTs = new Date(
              existing.updatedAt || existing.endedAt || existing.startedAt || 0
            ).getTime()
            const processTs = new Date(
              process.updatedAt || process.endedAt || process.startedAt || 0
            ).getTime()

            if (processTs >= existingTs) {
              latestByProcessId.set(process.processId, process)
            }
          }

          state.session.session.processes = Array.from(
            latestByProcessId.values()
          )

          return state
        },
        undefined,
        actionName("mergeSession")
      )
    },
    addProcess(process) {
      set(
        (state) => {
          state.session.session.processes.push(process)

          return state
        },
        undefined,
        actionName("addProcess")
      )
    },
    startProcess(processId) {
      set(
        (state) => {
          const process = state.session.session.processes.find(
            (proc) => proc.processId === processId
          )
          if (!process) {
            return
          }

          process.status = ShiftStatus.Started
          process.updatedAt = new Date().toISOString()

          return state
        },
        undefined,
        actionName("startProcess")
      )
    },

    setProcessUpdatedAt(processId, timestamp) {
      set(
        (state) => {
          const process = state.session.session.processes.find(
            (proc) => proc.processId === processId
          )
          if (!process) {
            return
          }

          process.updatedAt = timestamp
        },
        undefined,
        actionName("setProcessUpdatedAt")
      )
    },
    startShift(shiftSessionId) {
      set(
        (state) => {
          state.session.session.status = ShiftStatus.Started
          state.session.session.id = shiftSessionId

          return state
        },
        undefined,
        actionName("startShift")
      )
    },
    endProcess(processId, timestamp) {
      set(
        (state) => {
          const process = state.session.session.processes.find(
            (proc) => proc.processId === processId
          )
          if (!process) {
            return
          }

          const endedAt =
            process.endedAt || timestamp || new Date().toISOString()
          process.status = ShiftStatus.Ended
          process.endedAt = endedAt
          process.updatedAt = timestamp || endedAt

          return state
        },
        undefined,
        actionName("endProcess")
      )
    },
    setPendingIpqcDiscard(processId) {
      set(
        (state) => {
          state.session.pendingIpqcDiscardProcessId = processId

          return state
        },
        undefined,
        actionName("setPendingIpqcDiscard")
      )
    },
    clearPendingIpqcDiscard() {
      set(
        (state) => {
          state.session.pendingIpqcDiscardProcessId = ""

          return state
        },
        undefined,
        actionName("clearPendingIpqcDiscard")
      )
    },
    setPendingBoiloutDiscard(processId) {
      set(
        (state) => {
          state.session.pendingBoiloutDiscardProcessId = processId

          return state
        },
        undefined,
        actionName("setPendingBoiloutDiscard")
      )
    },
    clearPendingBoiloutDiscard() {
      set(
        (state) => {
          state.session.pendingBoiloutDiscardProcessId = ""

          return state
        },
        undefined,
        actionName("clearPendingBoiloutDiscard")
      )
    },
  },
})

export default createSessionSlice
